import Api from '../services/Api';
import { ErrorDictionary } from '../pages/Components/Helpers/ErrorDictionary';
import { Response } from './Types';

/**
 * Papéis de acesso: criar, renomear, excluir, escolher as telas e atrelar a usuários.
 *
 * As três travas do papel Administrador (não excluir, não perder tela, não ficar sem
 * nenhum usuário ativo) moram nas PROCEDURES do banco, não aqui. A tela só repassa a
 * mensagem que vier — duas regras para a mesma coisa divergem com o tempo, e a do banco
 * vale mesmo quando a chamada vem por outro caminho.
 */
class PapelController {
  public static async Listar(nome = ''): Promise<Response> {
    try {
      const response = await Api.post('api/papel/Listar', { Nome: nome });
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return PapelController.erro(error);
    }
  }

  public static async Gravar(roleId: number | null, nome: string): Promise<Response> {
    try {
      const response = await Api.post('api/papel/Gravar', { RoleId: roleId, Nome: nome });
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return PapelController.erro(error);
    }
  }

  public static async Excluir(roleId: number): Promise<Response> {
    try {
      const response = await Api.post(`api/papel/Excluir/${roleId}`, {});
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return PapelController.erro(error);
    }
  }

  /** As telas do papel, já com a marca do que está ligado. */
  public static async Telas(roleId: number): Promise<Response> {
    try {
      const response = await Api.get(`api/papel/Telas/${roleId}`);
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return PapelController.erro(error);
    }
  }

  /**
   * A lista INTEIRA do que ficou marcado — o que vem é a verdade, não um delta.
   *
   * `acoes` segue a mesma ideia, com uma sutileza que vale ler: uma tela com lista de
   * ações VAZIA fica SEM restrição, ou seja, com TODAS as ações — não com nenhuma.
   * Para o papel não fazer nada numa tela, basta não marcar a tela.
   *
   * Omitir `acoes` (undefined) não toca na restrição que já existe.
   */
  public static async TelasGravar(
    roleId: number,
    telas: string[],
    acoes?: { tela: string; acoes: string[] }[]
  ): Promise<Response> {
    try {
      const corpo: any = { RoleId: roleId, Telas: telas };
      if (acoes) corpo.Acoes = acoes;

      const response = await Api.post('api/papel/TelasGravar', corpo);
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return PapelController.erro(error);
    }
  }

  /** O catálogo de ações ativas, para desenhar as caixas. */
  public static async Acoes(): Promise<Response> {
    try {
      const response = await Api.get('api/acesso/Acoes');
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return PapelController.erro(error);
    }
  }

  public static async DoUsuario(usuarioId: number): Promise<Response> {
    try {
      const response = await Api.get(`api/papel/DoUsuario/${usuarioId}`);
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return PapelController.erro(error);
    }
  }

  public static async DoUsuarioGravar(usuarioId: number, papeis: string[]): Promise<Response> {
    try {
      const response = await Api.post('api/papel/DoUsuarioGravar', {
        UsuarioId: usuarioId,
        Papeis: papeis,
      });
      return { StatusCode: response.status, Data: response?.data };
    } catch (error: any) {
      return PapelController.erro(error);
    }
  }

  /**
   * A mensagem da procedure chega em ErroDetalhado e é ela que explica a recusa em
   * português — "o sistema ficaria sem nenhum administrador ativo", por exemplo. O texto
   * genérico do ErrorDictionary só entra quando não há mensagem nenhuma.
   */
  private static erro(error: any): Response {
    const corpo = error?.response?.data;
    const mensagem =
      corpo?.ErroDetalhado ||
      corpo?.Erro ||
      (typeof corpo === 'string' ? corpo : null) ||
      ErrorDictionary(error?.response?.status);

    return { StatusCode: error?.response?.status, Data: mensagem };
  }
}

export default PapelController;
